import { useState, useEffect, useRef, useCallback } from "react";
import { Mic, Volume2, VolumeX } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../styles/Personality.css";

// Opening line the coach leads with when no custom roadmap was passed in.
// The session is an open-ended conversation now — there is no fixed
// question list or question count to march through.
const OPENING_LINE =
  "Tell me about a moment you doubted yourself, but did the thing anyway.";

// Speech recognition is fixed to English — user speaks in English.
const STT_LANG = "en-IN";

// How long to wait after the last final speech chunk before treating the
// user as "done talking" and auto-submitting their answer.
const SILENCE_AUTOSUBMIT_MS = 1600;

const SESSION_SECONDS = 7 * 60;

// How long to show the "wrapping up" loading overlay before navigating away.
const FINISH_DELAY_MS = 1500;

// Strips markdown symbols so text-to-speech doesn't read out
// asterisks, hashes, backticks, etc.
function stripMarkdown(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/#+\s/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .trim();
}

// Splits text into speakable chunks (by paragraph, then by sentence if a
// paragraph is very long) so long coach replies are read in full instead
// of being cut off or truncated by the browser's speech engine.
function splitIntoChunks(text) {
  const paragraphs = text
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const chunks = [];
  for (const para of paragraphs) {
    if (para.length <= 200) {
      chunks.push(para);
    } else {
      const sentences = para.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [para];
      sentences.forEach((s) => {
        const trimmed = s.trim();
        if (trimmed) chunks.push(trimmed);
      });
    }
  }
  return chunks;
}

function CoachFace({ size = 44, speaking = false }) {
  return (
    <svg viewBox="0 0 44 44" fill="none" width={size} height={size}>
      <circle className="pdr-eye" cx="15" cy="19" r="2.4" fill="#fff" />
      <circle
        className="pdr-eye"
        cx="29"
        cy="19"
        r="2.4"
        fill="#fff"
        style={{ animationDelay: ".05s" }}
      />
      <path
        className={`pdr-mouth${speaking ? " pdr-mouth-talk" : ""}`}
        d="M14 28 Q22 33 30 28"
        stroke="#7FA0C9"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// The voice orb — signature element. A calm ring at rest, a gentle
// waveform in the accent color when the coach speaks, a soft breathing
// pulse while it listens, and a slow drifting shimmer while it thinks.
function VoiceOrb({ status, size = 128 }) {
  const speaking = status === "Speaking";
  const thinking = status === "Thinking";
  const listening = status === "Listening";

  return (
    <div
      className={`pdr-orb ${speaking ? "pdr-orb-speak" : ""} ${
        listening ? "pdr-orb-listen" : ""
      } ${thinking ? "pdr-orb-think" : ""}`}
      style={{ width: size, height: size }}
    >
      <div className="pdr-orb-ring pdr-orb-ring-1" />
      <div className="pdr-orb-ring pdr-orb-ring-2" />
      <div className="pdr-orb-core">
        <CoachFace speaking={speaking} size={Math.round(size * 0.4)} />
      </div>
    </div>
  );
}

let msgId = 0;
const nextId = () => `m${msgId++}`;

export default function PersonalityDevelopmentRoom() {
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [coachStatus, setCoachStatus] = useState("Listening");
  const [inputValue, setInputValue] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(SESSION_SECONDS);
  const [recognizing, setRecognizing] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [timeUp, setTimeUp] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const [finishing, setFinishing] = useState(false); // NEW: shows loading overlay before navigating

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const startedRef = useRef(false);
  const finalTranscriptRef = useRef("");
  const silenceTimerRef = useRef(null);
  const submitAnswerRef = useRef(() => {});

  const speechQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const spokenIndexRef = useRef(0);

  const recognizingRef = useRef(false);
  const isSendingRef = useRef(false);
  const sessionCompleteRef = useRef(false);
  const timeUpRef = useRef(false);
  const autoStartListeningRef = useRef(() => {});

  const navigate = useNavigate();
  const location = useLocation();
  const roadmap = location.state?.roadmap || "";

  const turnCount = messages.filter((m) => m.who === "user").length;

  const addMessage = useCallback((text, who) => {
    setMessages((prev) => [...prev, { id: nextId(), text, who }]);
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    addMessage(roadmap || OPENING_LINE, "coach");
  }, [addMessage, roadmap]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isTyping]);

  useEffect(() => {
    recognizingRef.current = recognizing;
  }, [recognizing]);
  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);
  useEffect(() => {
    sessionCompleteRef.current = sessionComplete;
  }, [sessionComplete]);

  useEffect(() => {
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [sessionKey]);

  useEffect(() => {
    if (secondsLeft !== 0 || timeUpRef.current || sessionCompleteRef.current) return;
    timeUpRef.current = true;

    if (window.speechSynthesis) window.speechSynthesis.cancel();
    speechQueueRef.current = [];
    isSpeakingRef.current = false;

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (recognitionRef.current && recognizingRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // already stopped — ignore
      }
    }
    setRecognizing(false);
    setIsTyping(false);
    setTimeUp(true);
    setSessionComplete(true);
    addMessage("Time's up! Your session has ended — tap Finish to continue.", "system");
  }, [secondsLeft, addMessage]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = STT_LANG;

    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          finalTranscriptRef.current = (finalTranscriptRef.current + " " + chunk).trim();
        } else {
          interim += chunk;
        }
      }
      const fullText = (finalTranscriptRef.current + " " + interim).trim();
      console.log("[speech-to-text]", JSON.stringify(fullText));

      setInputValue(fullText);

      if (fullText) {
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(() => {
          const text = (finalTranscriptRef.current || fullText).trim();
          silenceTimerRef.current = null;
          if (text) {
            recognition.stop();
            setRecognizing(false);
            submitAnswerRef.current(text);
          }
        }, SILENCE_AUTOSUBMIT_MS);
      }
    };
    recognition.onerror = (e) => {
      console.log("[speech-to-text][error]", e.error);
      setRecognizing(false);
    };
    recognition.onend = () => setRecognizing(false);
    recognitionRef.current = recognition;

    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  const processSpeechQueue = useCallback(() => {
    if (!window.speechSynthesis || speechQueueRef.current.length === 0) {
      isSpeakingRef.current = false;
      setCoachStatus("Listening");
      setTimeout(() => autoStartListeningRef.current(), 200);
      return;
    }
    isSpeakingRef.current = true;
    const chunk = speechQueueRef.current.shift();
    const utter = new SpeechSynthesisUtterance(chunk);
    utter.lang = "en-IN";
    utter.onstart = () => setCoachStatus("Speaking");
    utter.onend = () => processSpeechQueue();
    utter.onerror = () => processSpeechQueue();
    window.speechSynthesis.speak(utter);
  }, []);

  useEffect(() => {
    if (!voiceOn || !window.speechSynthesis) {
      const hadNewCoachMsg = messages
        .slice(spokenIndexRef.current)
        .some((m) => m.who === "coach");
      spokenIndexRef.current = messages.length;
      if (hadNewCoachMsg) {
        setTimeout(() => autoStartListeningRef.current(), 250);
      }
      return;
    }

    const newCoachMsgs = messages
      .slice(spokenIndexRef.current)
      .filter((m) => m.who === "coach");
    spokenIndexRef.current = messages.length;

    if (newCoachMsgs.length === 0) return;

    newCoachMsgs.forEach((m) => {
      const chunks = splitIntoChunks(stripMarkdown(m.text));
      speechQueueRef.current.push(...chunks);
    });

    if (!isSpeakingRef.current) {
      processSpeechQueue();
    }
  }, [messages, voiceOn, processSpeechQueue]);

  const autoStartListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (
      recognizingRef.current ||
      isSendingRef.current ||
      sessionCompleteRef.current ||
      isSpeakingRef.current
    )
      return;
    finalTranscriptRef.current = "";
    setInputValue("");
    recognition.lang = STT_LANG;
    setRecognizing(true);
    try {
      recognition.start();
    } catch (e) {
      setRecognizing(false);
    }
  }, []);

  useEffect(() => {
    autoStartListeningRef.current = autoStartListening;
  }, [autoStartListening]);

  useEffect(() => {
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      try {
        if (window.speechSynthesis) {
          const primer = new SpeechSynthesisUtterance(" ");
          primer.volume = 0;
          window.speechSynthesis.speak(primer);
        }
      } catch (e) {
        // ignore — priming is best-effort
      }
      if (
        !recognizingRef.current &&
        !isSendingRef.current &&
        !sessionCompleteRef.current
      ) {
        autoStartListeningRef.current();
      }
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden || sessionCompleteRef.current) return;

      if (window.speechSynthesis) window.speechSynthesis.cancel();
      speechQueueRef.current = [];
      isSpeakingRef.current = false;

      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      if (recognitionRef.current && recognizingRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // already stopped — ignore
        }
      }
      setRecognizing(false);
      setIsTyping(false);
      timeUpRef.current = true;
      setSessionComplete(true);
      navigate("/front");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [navigate]);

  const toggleMic = () => {
    if (sessionCompleteRef.current || isSpeakingRef.current) return;
    const recognition = recognitionRef.current;
    if (!recognition) {
      addMessage("Voice input isn't available in this browser — type your answer instead.", "system");
      return;
    }
    if (recognizing) {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      recognition.stop();
      setRecognizing(false);
    } else {
      finalTranscriptRef.current = "";
      recognition.lang = STT_LANG;
      setRecognizing(true);
      recognition.start();
    }
  };

  const submitAnswer = async (rawText) => {
    const text = rawText.trim();
    if (!text || isSending || sessionComplete) return;

    if (window.speechSynthesis) window.speechSynthesis.cancel();
    speechQueueRef.current = [];
    isSpeakingRef.current = false;

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    console.log("[user-answer]", JSON.stringify(text));
    setIsSending(true);
    addMessage(text, "user");
    setInputValue("");
    finalTranscriptRef.current = "";
    setCoachStatus("Thinking");
    setIsTyping(true);

    const sessionId = localStorage.getItem("growthSessionId");

    const response = await axiosClient.post(
      "/auth/api/users/api/mock-interview/query/personalDevelopment",
      {},
      {
        params: {
          sessionId,
          query: text,
        },
      }
    );

    const reply = response.data;

    setIsTyping(false);
    addMessage(reply, "coach");
    setCoachStatus("Listening");
    setIsSending(false);
  };

  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
  });

  // Stops all mic/speech activity, marks the session complete, shows a
  // brief "wrapping up" overlay, then routes to /front.
  const handleFinish = () => {
    if (finishing) return; // guard against double-clicks

    if (window.speechSynthesis) window.speechSynthesis.cancel();
    speechQueueRef.current = [];
    isSpeakingRef.current = false;
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (recognitionRef.current && recognizingRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // already stopped — ignore
      }
    }
    setRecognizing(false);
    timeUpRef.current = true;
    setSessionComplete(true);
    setFinishing(true);

    setTimeout(() => {
      navigate("/front");
    }, FINISH_DELAY_MS);
  };

  const restartSession = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    speechQueueRef.current = [];
    isSpeakingRef.current = false;
    spokenIndexRef.current = 0;

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (recognitionRef.current && recognizingRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // already stopped — ignore
      }
    }

    finalTranscriptRef.current = "";
    setRecognizing(false);
    setInputValue("");
    setIsSending(false);
    setIsTyping(false);
    setCoachStatus("Listening");
    setMessages([]);
    setSecondsLeft(SESSION_SECONDS);
    setSessionComplete(false);
    setTimeUp(false);
    timeUpRef.current = false;
    setSessionKey((k) => k + 1);

    addMessage(roadmap || OPENING_LINE, "coach");
  };

  const clockLabel = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(
    secondsLeft % 60
  ).padStart(2, "0")}`;
  const elapsedPct = ((SESSION_SECONDS - secondsLeft) / SESSION_SECONDS) * 100;
  const statusCopy =
    coachStatus === "Speaking"
      ? "Coach is speaking"
      : coachStatus === "Thinking"
      ? "Coach is thinking"
      : "Your turn to speak";

  return (
    <div
      className="pdr-page"
      style={{
        background: "var(--pdr-bg)",
        height: "100vh",
        width: "100vw",
        padding: "20px 28px",
        fontFamily: "'Inter', sans-serif",
        color: "var(--pdr-ink)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div className="pdr-ambient" />

      {/* NEW: Finish loading overlay */}
      {finishing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(245,246,248,0.94)",
            backdropFilter: "blur(2px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            zIndex: 999,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              border: "3px solid var(--pdr-border, #E2E5EA)",
              borderTopColor: "var(--pdr-amber, #3D5A80)",
              borderRadius: "50%",
              animation: "pdr-spin-loader .8s linear infinite",
            }}
          />
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--pdr-ink, #1C2333)" }}>
            Wrapping up your session…
          </div>
        </div>
      )}

      <style>{`
        :root {
          --pdr-bg: #F5F6F8;
          --pdr-card: #FFFFFF;
          --pdr-surface: #F1F3F6;
          --pdr-avatar-bg: #1C2333;
          --pdr-border: #E2E5EA;
          --pdr-border-soft: #EAEDF1;
          --pdr-ink: #1C2333;
          --pdr-amber: #3D5A80;
          --pdr-amber-soft: rgba(61,90,128,0.09);
          --pdr-amber-dim: rgba(61,90,128,0.3);
          --pdr-warn: #C2410C;
          --pdr-gray: #6B7280;
          --pdr-gray-light: #D8DCE3;
          --pdr-track: #E7EAEF;
          --pdr-green: #1F8A55;
          --pdr-green-soft: rgba(31,138,85,0.09);
          --pdr-user-solid: #1C2333;
          --radius-lg: 20px;
          --radius-md: 14px;
        }
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&family=Noto+Sans+Devanagari:wght@400;600&display=swap');

        html, body, #root { height: 100%; margin: 0; }
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
        h1, h2, h3, .pdr-heading-font { font-family: 'Sora', 'Inter', sans-serif; }

        .pdr-ambient {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(680px 420px at 10% -10%, rgba(61,90,128,0.05), transparent 60%),
            radial-gradient(560px 360px at 100% 6%, rgba(31,138,85,0.035), transparent 55%);
          z-index: 0;
        }

        .pdr-content { position: relative; z-index: 1; }

        @media (max-width: 640px) {
          .pdr-page { padding: 12px !important; }
        }

        .pdr-eye { animation: pdr-blink 4s infinite; transform-origin: center; }
        @keyframes pdr-blink {
          0%, 90%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }

        .pdr-bounce span {
          width: 5px; height: 5px; border-radius: 50%; background: var(--pdr-amber);
          animation: pdr-bounce 1s infinite ease-in-out; display: inline-block; margin: 0 2px;
        }
        .pdr-bounce span:nth-child(2) { animation-delay: .15s; }
        .pdr-bounce span:nth-child(3) { animation-delay: .3s; }
        @keyframes pdr-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: .4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        .pdr-pulse { animation: pdr-pulse-kf 1.5s infinite; }
        @keyframes pdr-pulse-kf {
          0% { box-shadow: 0 0 0 0 rgba(61,90,128,0.35); }
          70% { box-shadow: 0 0 0 12px rgba(61,90,128,0); }
          100% { box-shadow: 0 0 0 0 rgba(61,90,128,0); }
        }

        /* Voice orb — signature element */
        .pdr-orb {
          position: relative;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pdr-orb-core {
          position: relative;
          z-index: 2;
          width: 62%;
          height: 62%;
          border-radius: 50%;
          background: radial-gradient(circle at 32% 28%, #2A3550, var(--pdr-avatar-bg) 70%);
          border: 1px solid var(--pdr-border);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform .4s ease;
        }
        .pdr-orb-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1px solid var(--pdr-amber-dim);
          opacity: 0;
        }
        .pdr-orb-listen .pdr-orb-core { transform: scale(1); }
        .pdr-orb-listen .pdr-orb-ring-1 {
          opacity: 1;
          animation: pdr-orb-breathe 2.6s ease-in-out infinite;
        }
        .pdr-orb-speak .pdr-orb-core { transform: scale(1.04); }
        .pdr-orb-speak .pdr-orb-ring-1,
        .pdr-orb-speak .pdr-orb-ring-2 {
          opacity: 1;
          border-color: var(--pdr-amber);
          animation: pdr-orb-wave 1.4s ease-out infinite;
        }
        .pdr-orb-speak .pdr-orb-ring-2 { animation-delay: .5s; }
        .pdr-orb-think .pdr-orb-ring-1 {
          opacity: .8;
          border-style: dashed;
          animation: pdr-orb-spin 3.5s linear infinite;
        }
        @keyframes pdr-orb-breathe {
          0%, 100% { transform: scale(0.94); opacity: .5; }
          50% { transform: scale(1.06); opacity: .9; }
        }
        @keyframes pdr-orb-wave {
          0% { transform: scale(0.9); opacity: .9; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes pdr-orb-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .pdr-wave { display: flex; align-items: flex-end; gap: 2.5px; height: 11px; }
        .pdr-wave span {
          width: 2.5px; background: var(--pdr-amber); border-radius: 1px;
          animation: pdr-wave-kf .8s infinite ease-in-out;
        }
        .pdr-wave span:nth-child(1) { animation-delay: 0s; }
        .pdr-wave span:nth-child(2) { animation-delay: .15s; }
        .pdr-wave span:nth-child(3) { animation-delay: .3s; }
        .pdr-wave span:nth-child(4) { animation-delay: .45s; }
        @keyframes pdr-wave-kf {
          0%, 100% { height: 3px; }
          50% { height: 11px; }
        }

        .pdr-messages::-webkit-scrollbar { width: 5px; }
        .pdr-messages::-webkit-scrollbar-thumb { background: var(--pdr-border); border-radius: 10px; }

        .btn-action { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        .btn-action:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(16,24,40,0.12); }
        .btn-action:active:not(:disabled) { transform: translateY(0); }
        .btn-action:focus-visible { outline: 2px solid var(--pdr-amber); outline-offset: 2px; }

        .pdr-toggle { transition: color .2s, background .2s; }

        .pdr-devanagari { font-family: 'Noto Sans Devanagari', 'Inter', sans-serif; }
        .pdr-devanagari p { margin: 0 0 4px 0; }
        .pdr-devanagari p:empty { display: none; margin: 0; }
        .pdr-devanagari p:last-child { margin-bottom: 0; }
        .pdr-devanagari ul, .pdr-devanagari ol { margin: 2px 0 6px 0; padding-left: 18px; }
        .pdr-devanagari li { margin-bottom: 2px; }
        .pdr-devanagari li:last-child { margin-bottom: 0; }
        .pdr-devanagari li > p { margin: 0; }
        .pdr-devanagari h1, .pdr-devanagari h2, .pdr-devanagari h3 { margin: 6px 0 4px 0; font-size: 1em; }
        .pdr-devanagari h1:first-child, .pdr-devanagari h2:first-child, .pdr-devanagari h3:first-child { margin-top: 0; }
        .pdr-devanagari blockquote { margin: 4px 0; padding-left: 10px; border-left: 2px solid var(--pdr-border); }
        .pdr-devanagari strong { font-weight: 700; color: var(--pdr-ink); }

        /* NEW: spinner for the Finish overlay */
        @keyframes pdr-spin-loader { to { transform: rotate(360deg); } }

        @media (prefers-reduced-motion: reduce) {
          .pdr-orb-ring, .pdr-orb-core, .pdr-eye, .pdr-bounce span, .pdr-wave span, .pdr-pulse {
            animation: none !important;
          }
        }
      `}</style>

      <div
        className="pdr-content"
        style={{
          maxWidth: 1360,
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
            flexWrap: "wrap",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              title="CodeMatrix"
              style={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                background: "var(--pdr-surface)",
                border: "1px solid var(--pdr-border)",
              }}
            >
              <img
                src="codematrixLogo.png"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <div>
              <h1
                style={{
                  fontWeight: 600,
                  fontSize: 22,
                  letterSpacing: "-0.3px",
                  margin: 0,
                  color: "var(--pdr-ink)",
                }}
              >
                Personality Development
              </h1>
              <div style={{ fontSize: 12.5, color: "var(--pdr-gray)", fontWeight: 500, marginTop: 2 }}>
                {sessionComplete ? "Session complete" : statusCopy}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                background: "var(--pdr-surface)",
                border: "1px solid var(--pdr-border)",
                color: secondsLeft <= 60 ? "var(--pdr-warn)" : "var(--pdr-ink)",
                borderRadius: "30px",
                padding: "7px 16px",
                fontSize: 13,
                fontWeight: 600,
                transition: "color .3s",
              }}
            >
              {clockLabel}
            </div>

            {/* NEW: Finish button — available at any point in the session */}
            {!sessionComplete && (
              <button
                onClick={handleFinish}
                className="btn-action"
                style={{
                  background: "var(--pdr-ink)",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "30px",
                  padding: "7px 18px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Finish
              </button>
            )}
          </div>
        </header>

        {/* Grid Layout */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 18, flex: 1, minHeight: 0 }}
          className="pdr-grid"
        >
          <style>{`@media (max-width: 820px){ .pdr-grid{ grid-template-columns: 1fr !important; gap: 16px !important; } }`}</style>

          {/* Main Chat Card */}
          <div
            style={{
              background: "var(--pdr-card)",
              border: "1px solid var(--pdr-border)",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(16,24,40,0.06)",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* Elapsed strip — reflects time in the session, not a question count */}
            <div
              style={{
                padding: "14px 22px",
                borderBottom: "1px solid var(--pdr-border-soft)",
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--pdr-amber)",
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {turnCount === 0 ? "Getting started" : `${turnCount} exchange${turnCount === 1 ? "" : "s"} so far`}
              </div>
              <div style={{ flex: 1, height: 4, background: "var(--pdr-track)", borderRadius: 10, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    background: "linear-gradient(90deg, var(--pdr-amber), #7FA0C9)",
                    borderRadius: 10,
                    width: `${elapsedPct}%`,
                    transition: "width 1s linear",
                  }}
                />
              </div>
            </div>

            {/* Message Area */}
            <div
              className="pdr-messages"
              style={{
                padding: "20px 24px",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {messages.map((m) => (
                <MessageRow key={m.id} who={m.who} text={m.text} />
              ))}

              {isTyping && (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "var(--pdr-avatar-bg)",
                      border: "1px solid var(--pdr-border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CoachFace size={15} />
                  </div>
                  <div
                    style={{
                      background: "var(--pdr-amber-soft)",
                      borderRadius: "12px",
                      padding: "10px 14px",
                      display: "inline-block",
                    }}
                  >
                    <div className="pdr-bounce">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Bottom Actions Panel */}
            <div style={{ borderTop: "1px solid var(--pdr-border)", padding: "16px 20px", background: "var(--pdr-surface)" }}>
              {sessionComplete ? (
                <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                  <button
                    onClick={handleFinish}
                    disabled={finishing}
                    className="btn-action"
                    style={{
                      background: "var(--pdr-amber)",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      padding: "0 28px",
                      height: 44,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: finishing ? "not-allowed" : "pointer",
                      opacity: finishing ? 0.7 : 1,
                    }}
                  >
                    {finishing ? "Finishing…" : "Finish →"}
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <button
                    onClick={toggleMic}
                    disabled={coachStatus === "Thinking" || coachStatus === "Speaking"}
                    className={`btn-action ${recognizing ? "pdr-pulse" : ""}`}
                    style={{
                      background:
                        coachStatus === "Thinking" || coachStatus === "Speaking"
                          ? "var(--pdr-gray-light)"
                          : recognizing
                          ? "var(--pdr-amber)"
                          : "var(--pdr-ink)",
                      color:
                        coachStatus === "Thinking" || coachStatus === "Speaking"
                          ? "var(--pdr-gray)"
                          : recognizing
                          ? "#FFFFFF"
                          : "var(--pdr-bg)",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      padding: "0 36px",
                      height: 50,
                      fontWeight: 600,
                      fontSize: 14.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: coachStatus === "Thinking" || coachStatus === "Speaking" ? "not-allowed" : "pointer",
                      boxShadow: recognizing ? "0 4px 16px rgba(61,90,128,0.25)" : "0 4px 14px rgba(16,24,40,0.14)",
                    }}
                  >
                    <Mic size={15} />
                    {coachStatus === "Thinking"
                      ? "Coach is thinking…"
                      : coachStatus === "Speaking"
                      ? "Coach is speaking — your turn next"
                      : recognizing
                      ? "Listening…"
                      : "Mic ready — just speak"}
                  </button>
                </div>
              )}

              {!sessionComplete && inputValue && (
                <div
                  className="pdr-devanagari"
                  style={{
                    marginTop: 12,
                    textAlign: "center",
                    fontSize: 13.5,
                    color: "var(--pdr-ink)",
                    background: "var(--pdr-card)",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px dashed var(--pdr-border)",
                    fontStyle: "italic",
                  }}
                >
                  "{inputValue}"
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 14,
                  fontSize: 12.5,
                  color: "var(--pdr-gray)",
                  fontWeight: 500,
                }}
              >
                <span
                  className="pdr-toggle"
                  style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", userSelect: "none" }}
                  onClick={() => {
                    setVoiceOn((v) => {
                      if (v && window.speechSynthesis) {
                        window.speechSynthesis.cancel();
                        speechQueueRef.current = [];
                        isSpeakingRef.current = false;
                      }
                      return !v;
                    });
                  }}
                >
                  {voiceOn ? <Volume2 size={13} style={{ color: "var(--pdr-amber)" }} /> : <VolumeX size={13} />}
                  Voice replies:{" "}
                  <span style={{ color: voiceOn ? "var(--pdr-amber)" : "inherit", fontWeight: 600 }}>
                    {voiceOn ? "ON" : "OFF"}
                  </span>
                </span>
                {!sessionComplete && (
                  <span
                    className="pdr-toggle"
                    onClick={restartSession}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Restart session
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Coach Panel — voice orb as the centerpiece */}
          <div
            style={{
              background: "var(--pdr-card)",
              border: "1px solid var(--pdr-border)",
              padding: "30px 18px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(16,24,40,0.06)",
              borderRadius: "var(--radius-lg)",
              minHeight: 0,
            }}
          >
            <VoiceOrb status={sessionComplete ? "Idle" : coachStatus} size={128} />

            <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 18, color: "var(--pdr-ink)", marginTop: 16 }}>
              AI Coach
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                fontWeight: 700,
                marginTop: 8,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                padding: "4px 10px",
                borderRadius: "20px",
                background:
                  coachStatus === "Speaking"
                    ? "var(--pdr-amber-soft)"
                    : coachStatus === "Thinking"
                    ? "var(--pdr-track)"
                    : "var(--pdr-green-soft)",
                color:
                  coachStatus === "Speaking"
                    ? "var(--pdr-amber)"
                    : coachStatus === "Thinking"
                    ? "var(--pdr-gray)"
                    : "var(--pdr-green)",
              }}
            >
              {coachStatus === "Speaking" ? (
                <span className="pdr-wave">
                  <span />
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: coachStatus === "Thinking" ? "var(--pdr-gray)" : "var(--pdr-green)",
                    display: "inline-block",
                  }}
                />
              )}
              {sessionComplete ? "Complete" : coachStatus}
            </div>

            <div style={{ width: "100%", height: 1, background: "var(--pdr-border)", margin: "24px 0 20px" }} />

            <div style={{ width: "100%", display: "flex", gap: 10 }}>
              <div
                style={{
                  flex: 1,
                  background: "var(--pdr-surface)",
                  border: "1px solid var(--pdr-border-soft)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px 8px",
                }}
              >
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, fontWeight: 600, color: "var(--pdr-amber)" }}>
                  {turnCount}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--pdr-gray)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 2 }}>
                  Exchanges
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  background: "var(--pdr-surface)",
                  border: "1px solid var(--pdr-border-soft)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px 8px",
                }}
              >
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 20, fontWeight: 600, color: "var(--pdr-ink)" }}>
                  {clockLabel}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--pdr-gray)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 2 }}>
                  Remaining
                </div>
              </div>
            </div>

            <div style={{ flex: 1 }} />

            <div
              style={{
                width: "100%",
                background: "var(--pdr-amber-soft)",
                borderRadius: "var(--radius-md)",
                padding: "12px 14px",
                textAlign: "left",
                fontSize: 12.5,
                color: "#2C4870",
                lineHeight: 1.5,
                fontWeight: 500,
              }}
            >
              💡 Speak naturally — the mic opens as soon as the coach finishes talking, and your answer sends itself after a short pause.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeSpacing(text) {
  return (text || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function MessageRow({ who, text }) {
  if (who === "coach") {
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "var(--pdr-avatar-bg)",
            border: "1px solid var(--pdr-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CoachFace size={15} />
        </div>
        <div
          className="pdr-devanagari"
          style={{
            maxWidth: "85%",
            padding: "14px 18px",
            borderRadius: "4px 16px 16px 16px",
            background: "var(--pdr-surface)",
            border: "1px solid var(--pdr-border-soft)",
            boxShadow: "0 1px 4px rgba(16,24,40,0.05)",
            lineHeight: "1.45",
            fontSize: "14px",
            color: "var(--pdr-ink)",
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeSpacing(text)}</ReactMarkdown>
        </div>
      </div>
    );
  }

  if (who === "user") {
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "flex-end" }}>
        <div
          className="pdr-devanagari"
          style={{
            maxWidth: "75%",
            padding: "12px 16px",
            borderRadius: "16px 4px 16px 16px",
            background: "var(--pdr-user-solid)",
            color: "#F5F6F8",
            fontSize: "14.5px",
            lineHeight: 1.5,
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(16,24,40,0.12)",
          }}
        >
          {text}
        </div>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "var(--pdr-amber)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 13,
          }}
        >
          🙋
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ width: 30, flexShrink: 0 }} />
      <div
        className="pdr-devanagari"
        style={{
          maxWidth: "80%",
          padding: "10px 14px",
          borderRadius: "12px",
          background: "var(--pdr-amber-soft)",
          color: "var(--pdr-amber)",
          border: "1px dashed var(--pdr-amber-dim)",
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        {text}
      </div>
    </div>
  );
}